import { OpcaoRenda } from '../../enum/parametros/opcao-renda.enum';
import { VariavelValorMinimo } from '../../enum/parametros/variavel-valor-minimo.enum';
import {
  CatalogosElegibilidade,
  CombinacaoIdadeVinculacao,
  Antecipacao,
  OpcoesRenda,
  ParametrosOpcoesRenda,
  TipoValorMinimoRenda,
  ConfiguracaoBeneficiarios,
  RegraImpedimento,
  RegraValorMinimoSaldo,
  RegrasGeraisElegibilidade,
  Rentabilidade,
  UnidadeTempo,
  ValorMinimoRenda,
} from '../../models/parametros/beneficios.model';

export type SecaoBeneficios = 'visao-geral' | 'elegibilidade' | 'opcoes-renda' | 'antecipacao' | 'rentabilidade' | 'valor-minimo-saldo';

/** Ícones do conjunto feather. */
export const SECOES_BENEFICIOS: readonly { id: SecaoBeneficios; rotulo: string; icone: string }[] = [
  { id: 'visao-geral', rotulo: 'Visão Geral', icone: 'grid' },
  { id: 'elegibilidade', rotulo: 'Elegibilidade', icone: 'user-check' },
  { id: 'opcoes-renda', rotulo: 'Opções de Renda', icone: 'trending-up' },
  { id: 'antecipacao', rotulo: 'Antecipação', icone: 'fast-forward' },
  { id: 'rentabilidade', rotulo: 'Rentabilidade', icone: 'bar-chart-2' },
  { id: 'valor-minimo-saldo', rotulo: 'Regra valor mínimo do saldo', icone: 'shield' },
];

/** Seções já disponíveis; as demais aparecem desabilitadas ("Disponível em breve"). */
export const SECOES_BENEFICIOS_DISPONIVEIS: ReadonlySet<SecaoBeneficios> = new Set(['elegibilidade', 'opcoes-renda', 'antecipacao', 'rentabilidade', 'valor-minimo-saldo']);

/** O mesmo limite da API (`desc_excecao`). */
export const EXCECOES_ELEGIBILIDADE_MAXIMO = 500;

/** O mesmo limite da API (`desc_observacao`). */
export const OBSERVACOES_ELEGIBILIDADE_MAXIMO = 500;

/** O mesmo limite das mensagens das regras do extrato. */
export const MENSAGEM_IMPEDIMENTO_MAXIMO = 300;

/** Limite dos campos em anos (idade e tempos mínimos). */
export const ANOS_MAXIMO = 120;

export const UNIDADES_TEMPO_OPCOES: readonly { value: UnidadeTempo; label: string }[] = [
  { value: 'anos', label: 'Anos' },
  { value: 'meses', label: 'Meses' },
];

/** O mesmo prazo de `ANOS_MAXIMO`, em cada unidade. */
export const TEMPO_SERVICO_MAXIMO: Readonly<Record<UnidadeTempo, number>> = { anos: 120, meses: 1440 };

export const BENEFICIARIOS_MAXIMO = 99;

/** Limite do prazo certo da renda em cada unidade (o mesmo prazo: 50 anos). */
export const PRAZO_CERTO_MAXIMO: Readonly<Record<UnidadeTempo, number>> = { anos: 50, meses: 600 };

/** Verificações que o sistema sempre faz; não são configuráveis na tela. */
export const VALIDACOES_AUTOMATICAS: readonly string[] = [
  'O participante pertence a um plano habilitado para o benefício.',
  'O benefício está parametrizado e disponível para o plano.',
  'O participante não tem o benefício já concedido (salvo indeferimento).',
  'A solicitação está dentro da janela operacional definida pela Vivest.',
];

export const REGRAS_ELEGIBILIDADE_PADRAO: RegrasGeraisElegibilidade = {
  desligamentoObrigatorio: false,
  situacoesElegiveis: [],
  tempoMinimoServico: null,
  unidadeTempoServico: 'anos',
  combinacoes: [],
  comprovacoes: [],
  beneficiarios: { quantidade: null, percentualMinimo: null },
  impedimentos: [],
  observacoes: '',
};

const rotulo = <T>(opcoes: readonly { value: T; label: string }[], valor: T): string =>
  opcoes.find((opcao) => opcao.value === valor)?.label ?? String(valor);

const formatoPercentual = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

/** 5 → "5 anos"; 1 → "1 ano". */
export function anos(valor: number): string {
  return `${valor} ${valor === 1 ? 'ano' : 'anos'}`;
}

/** 6 meses → "6 meses"; 1 mês → "1 mês"; em anos, como `anos`. */
export function tempo(valor: number, unidade: UnidadeTempo): string {
  if (unidade === 'anos') {
    return anos(valor);
  }
  return `${valor} ${valor === 1 ? 'mês' : 'meses'}`;
}

/** { 55, 5 } → "55 anos + 5 anos de vinculação"; só idade → "55 anos de idade". */
export function descreverCombinacao({ idadeMinima, tempoMinimoVinculacao }: CombinacaoIdadeVinculacao): string {
  const vinculacao = tempoMinimoVinculacao !== null ? `${anos(tempoMinimoVinculacao)} de vinculação` : '';
  if (idadeMinima === null) {
    return vinculacao;
  }
  return vinculacao ? `${anos(idadeMinima)} + ${vinculacao}` : `${anos(idadeMinima)} de idade`;
}

/** ["A", "B", "C"] → "A, B ou C" (ou com outro conectivo). */
export function juntar(itens: readonly string[], conectivo = 'ou'): string {
  return itens.length > 1 ? `${itens.slice(0, -1).join(', ')} ${conectivo} ${itens[itens.length - 1]}` : (itens[0] ?? '');
}

/** Condições do impedimento, lidas unidas por "e"; ids sem nome são ignorados. */
export function condicoesImpedimento(regra: RegraImpedimento, nomesBeneficios: ReadonlyMap<number, string>): string[] {
  const condicoes = regra.beneficios
    .map((id) => nomesBeneficios.get(id))
    .filter((nome): nome is string => !!nome)
    .map((nome) => `Elegível a ${nome}`);
  if (regra.idadeMinima !== null) {
    condicoes.push(`Idade mínima de ${anos(regra.idadeMinima)}`);
  }
  if (regra.idadeMaxima !== null) {
    condicoes.push(`Idade máxima de ${anos(regra.idadeMaxima)}`);
  }
  return condicoes;
}

/** "Impedido quando: elegível a Aposentadoria Normal e idade mínima de 60 anos"; sem condição → "". */
export function descreverImpedimento(regra: RegraImpedimento, nomesBeneficios: ReadonlyMap<number, string>): string {
  const condicoes = condicoesImpedimento(regra, nomesBeneficios).map(
    (condicao) => condicao.charAt(0).toLowerCase() + condicao.slice(1),
  );
  return condicoes.length ? `Impedido quando: ${juntar(condicoes, 'e')}` : '';
}

/** { 3, 10 } → "Até 3 beneficiários, com no mínimo 10% cada"; sem limites → "". */
export function descreverBeneficiarios({ quantidade, percentualMinimo }: ConfiguracaoBeneficiarios): string {
  const minimo = percentualMinimo !== null ? `no mínimo ${formatoPercentual.format(percentualMinimo)}% cada` : '';
  if (quantidade === null) {
    return minimo ? `Beneficiários com ${minimo}` : '';
  }
  const limite = `Até ${quantidade} ${quantidade === 1 ? 'beneficiário' : 'beneficiários'}`;
  return minimo ? `${limite}, com ${minimo}` : limite;
}

/**
 * Uma linha por condição obrigatória; as alternativas da linha vêm unidas por "ou". Situações e
 * comprovações levam o rótulo do catálogo.
 */
export function descreverRegras(
  regras: RegrasGeraisElegibilidade,
  nomesBeneficios: ReadonlyMap<number, string>,
  catalogos: CatalogosElegibilidade,
): string[] {
  const linhas: string[] = [];
  if (regras.desligamentoObrigatorio) {
    linhas.push('Desligado da patrocinadora');
  }
  if (regras.situacoesElegiveis.length) {
    linhas.push(`Situação: ${juntar(regras.situacoesElegiveis.map((s) => rotulo(catalogos.situacoes, s)))}`);
  }
  const combinacoes = regras.combinacoes.map(descreverCombinacao).filter(Boolean);
  if (combinacoes.length) {
    linhas.push(juntar(combinacoes));
  }
  if (regras.tempoMinimoServico !== null) {
    linhas.push(`${tempo(regras.tempoMinimoServico, regras.unidadeTempoServico)} de serviço na patrocinadora`);
  }
  if (regras.comprovacoes.length) {
    linhas.push(juntar(regras.comprovacoes.map((c) => rotulo(catalogos.comprovacoes, c))));
  }
  linhas.push(...regras.impedimentos.map((regra) => descreverImpedimento(regra, nomesBeneficios)).filter(Boolean));
  const beneficiarios = descreverBeneficiarios(regras.beneficiarios);
  if (beneficiarios) {
    linhas.push(beneficiarios);
  }
  return linhas;
}

// ── opções de renda ──

export const OPCOES_RENDA_OPCOES: readonly { value: OpcaoRenda; label: string }[] = [
  { value: OpcaoRenda.ValorFixoMensal, label: 'Valor fixo mensal' },
  { value: OpcaoRenda.PercentualSaldo, label: 'Percentual de saldo' },
  { value: OpcaoRenda.PrazoCerto, label: 'Prazo certo' },
  { value: OpcaoRenda.PagamentoUnico, label: 'Pagamento único' },
];

export const TIPOS_VALOR_MINIMO_RENDA_OPCOES: readonly { value: TipoValorMinimoRenda; label: string }[] = [
  { value: 'valor', label: 'Valor mínimo inicial (R$)' },
  { value: 'percentual', label: 'Percentual sobre o saldo (%)' },
];

/** Plano sem opções de renda configuradas: tudo em branco. */
export const OPCOES_RENDA_PADRAO: OpcoesRenda = {
  opcoes: [],
  parametros: { percentualSaldo: { minimo: null, maximo: null }, prazoCerto: { minimo: null, maximo: null, unidade: 'anos' } },
  valorMinimo: { validar: false, tipo: 'valor', valor: null, percentual: null },
};

const DUAS_CASAS = /^\d+(\.\d{1,2})?$/;

/** Faixa do percentual do saldo: de 0 a 100, com até duas casas decimais. */
export const percentualSaldoOk = (valor: number | null) =>
  valor !== null && valor >= 0 && valor <= 100 && DUAS_CASAS.test(String(valor));

/** Renda mínima em percentual do saldo: de 0,01 a 100, com até duas casas decimais. */
export const percentualRendaMinimaOk = (valor: number | null) =>
  valor !== null && valor > 0 && valor <= 100 && DUAS_CASAS.test(String(valor));

/** Renda mínima em reais: maior que zero (a máscara já limita a duas casas). */
export const valorRendaMinimaOk = (valor: number | null) => valor !== null && valor > 0;

/** Prazo certo: inteiro de 1 até o limite da unidade. */
export const prazoCertoOk = (valor: number | null, unidade: UnidadeTempo) =>
  valor !== null && Number.isInteger(valor) && valor >= 1 && valor <= PRAZO_CERTO_MAXIMO[unidade];

/** Erro dos valores de uma opção marcada; vazio quando a opção não pede valores ou está correta. */
export function erroOpcaoRenda(opcao: OpcaoRenda, { percentualSaldo, prazoCerto }: ParametrosOpcoesRenda): string {
  switch (opcao) {
    case OpcaoRenda.PercentualSaldo:
      if (!percentualSaldoOk(percentualSaldo.minimo) || !percentualSaldoOk(percentualSaldo.maximo)) {
        return 'Informe os percentuais de 0 a 100, com até duas casas decimais.';
      }
      return percentualSaldo.minimo! > percentualSaldo.maximo! ? 'O percentual mínimo não pode ser maior que o máximo.' : '';
    case OpcaoRenda.PrazoCerto: {
      const { minimo, maximo, unidade } = prazoCerto;
      if (!prazoCertoOk(minimo, unidade) || !prazoCertoOk(maximo, unidade)) {
        return `Informe o prazo mínimo e o máximo, de 1 a ${PRAZO_CERTO_MAXIMO[unidade]} ${unidade}.`;
      }
      return minimo! > maximo! ? 'O prazo mínimo não pode ser maior que o máximo.' : '';
    }
    default:
      return '';
  }
}

/** Com a validação ligada, o mínimo do tipo escolhido é obrigatório: valor > 0 ou percentual de 0,01 a 100. */
export function valorMinimoRendaOk({ validar, tipo, valor, percentual }: ValorMinimoRenda): boolean {
  if (!validar) {
    return true;
  }
  return tipo === 'valor' ? valorRendaMinimaOk(valor) : percentualRendaMinimaOk(percentual);
}

const formatoPercentualSaldo = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const formatoMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** "Percentual de saldo (de 1% a 10%)", "Prazo certo (de 5 a 20 anos)". */
function descreverOpcaoRenda(opcao: OpcaoRenda, { percentualSaldo, prazoCerto }: ParametrosOpcoesRenda): string {
  const nome = rotulo(OPCOES_RENDA_OPCOES, opcao);
  if (opcao === OpcaoRenda.PercentualSaldo && percentualSaldo.minimo !== null && percentualSaldo.maximo !== null) {
    const { minimo, maximo } = percentualSaldo;
    return `${nome} (de ${formatoPercentualSaldo.format(minimo)}% a ${formatoPercentualSaldo.format(maximo)}%)`;
  }
  if (opcao === OpcaoRenda.PrazoCerto && prazoCerto.minimo !== null && prazoCerto.maximo !== null) {
    return `${nome} (de ${prazoCerto.minimo} a ${tempo(prazoCerto.maximo, prazoCerto.unidade)})`;
  }
  return nome;
}

/** Resumo das opções de renda, uma condição por linha. */
export function descreverOpcoesRenda({ opcoes, parametros, valorMinimo }: OpcoesRenda): string[] {
  const linhas: string[] = [];
  if (opcoes.length) {
    linhas.push(`Opções: ${juntar(opcoes.map((opcao) => descreverOpcaoRenda(opcao, parametros)))}`);
  }
  const { validar, tipo, valor, percentual } = valorMinimo;
  if (validar && tipo === 'valor' && valor !== null) {
    linhas.push(`Renda mínima de ${formatoMoeda.format(valor).replace(/\u00a0/g, ' ')}`);
  } else if (validar && tipo === 'percentual' && percentual !== null) {
    linhas.push(`Renda mínima de ${formatoPercentualSaldo.format(percentual)}% do saldo`);
  } else {
    linhas.push('Sem valor mínimo de renda');
  }
  return linhas;
}

// ── antecipação ──

/** Plano sem antecipação configurada: tudo desligado e em branco. */
export const ANTECIPACAO_PADRAO: Antecipacao = {
  saldo: { permitir: false, percentualMinimo: null, percentualMaximo: null },
  adiantamentoBeneficio: { elegivel: false, percentual: null },
};

/** Resumo da antecipação, uma condição por linha. */
export function descreverAntecipacao({ saldo, adiantamentoBeneficio }: Antecipacao): string[] {
  const { permitir, percentualMinimo, percentualMaximo } = saldo;
  const linhas: string[] = [];
  if (!permitir) {
    linhas.push('Antecipação de saldo não permitida');
  } else if (percentualMinimo !== null && percentualMaximo !== null) {
    linhas.push(`Antecipação de ${formatoPercentualSaldo.format(percentualMinimo)}% a ${formatoPercentualSaldo.format(percentualMaximo)}% do saldo`);
  } else {
    linhas.push('Antecipação de saldo permitida');
  }
  if (!adiantamentoBeneficio.elegivel) {
    linhas.push('Sem adiantamento de benefício');
  } else if (adiantamentoBeneficio.percentual !== null) {
    linhas.push(`Adiantamento de ${formatoPercentualSaldo.format(adiantamentoBeneficio.percentual)}% do benefício`);
  } else {
    linhas.push('Elegível para adiantamento de benefício');
  }
  return linhas;
}

// ── rentabilidade ──

export const RENTABILIDADE_PADRAO: Rentabilidade = { percentualMinimo: null, percentualMaximo: null, percentualPadrao: null };

export function descreverRentabilidade({ percentualMinimo, percentualMaximo, percentualPadrao }: Rentabilidade): string[] {
  return [
    percentualMinimo !== null && percentualMaximo !== null
      ? `Rentabilidade de ${formatoPercentualSaldo.format(percentualMinimo)}% a ${formatoPercentualSaldo.format(percentualMaximo)}%`
      : 'Faixa de rentabilidade não definida',
    percentualPadrao !== null ? `Valor padrão de ${formatoPercentualSaldo.format(percentualPadrao)}%` : 'Valor padrão não definido',
  ];
}

// ── regra de valor mínimo do saldo ──

export const VARIAVEIS_VALOR_MINIMO_OPCOES: readonly { value: VariavelValorMinimo; label: string }[] = [
  { value: VariavelValorMinimo.Salario, label: 'Salário' },
  { value: VariavelValorMinimo.SalarioMinimo, label: 'Salário Mínimo' },
  { value: VariavelValorMinimo.Saldo, label: 'Saldo' },
  { value: VariavelValorMinimo.UnidadePrevidenciaria, label: 'Unidade Previdenciária' },
];

export const REGRA_VALOR_MINIMO_SALDO_PADRAO: RegraValorMinimoSaldo = {
  habilitada: false,
  tipo: 'fixo',
  valor: null,
  variavel: null,
  multiplicador: null,
};

export function descreverRegraValorMinimoSaldo({ habilitada, tipo, valor, variavel, multiplicador }: RegraValorMinimoSaldo): string[] {
  if (!habilitada) {
    return ['Regra de valor mínimo do saldo desabilitada'];
  }
  if (tipo === 'fixo') {
    return valor !== null
      ? [`Valor mínimo de recebimento de ${formatoMoeda.format(valor).replace(/\u00a0/g, ' ')}`]
      : ['Valor mínimo de recebimento não definido'];
  }
  return variavel && multiplicador !== null
    ? [`Valor mínimo de ${formatoPercentualSaldo.format(multiplicador)} × ${rotulo(VARIAVEIS_VALOR_MINIMO_OPCOES, variavel)}`]
    : ['Regra variável não definida'];
}
